import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { useTranslation } from 'react-i18next';
import { isRtlLang } from '../i18n';

interface CarouselProps {
  items: Array<{
    id: number;
    content?: React.ReactNode;
    imageUrl?: string;
    name?: string;
    title?: string;
    review?: string;
  }>;
  type: 'image' | 'review' | 'service';
  isRTL?: boolean;
  customNavigation?: boolean;
}

const Carousel: React.FC<CarouselProps> = ({
  items,
  type,
  customNavigation = false,
}) => {
  const { i18n } = useTranslation();
  const [isRTL, setIsRTL] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setIsRTL(isRtlLang(i18n.language));
    setLoading(true);
    window.setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [i18n.language]);

  const navigationProps = customNavigation
    ? {
        navigation: {
          prevEl: '.swiper-button-prev-custom',
          nextEl: '.swiper-button-next-custom',
        },
      }
    : { navigation: true };

  return loading ? (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-600"></div>
    </div>
  ) : (
    <>
      <Swiper
        modules={[Autoplay, Pagination, Navigation]}
        spaceBetween={type === 'service' ? 20 : 30}
        slidesPerView={1}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          reverseDirection: !isRTL,
          waitForTransition: true,
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active',
        }}
        {...navigationProps}
        breakpoints={
          type === 'review' || type === 'service'
            ? {
                640: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 40,
                },
              }
            : {}
        }
        dir={isRTL ? 'rtl' : 'ltr'}
        className={`${
          type === 'service' ? 'pb-12' : 'mySwiper'
        } [&_.swiper-pagination]:!relative [&_.swiper-pagination]:!mt-8 [&_.swiper-pagination-bullet]:!w-2.5 [&_.swiper-pagination-bullet]:!h-2.5 [&_.swiper-pagination-bullet]:!bg-gray-300 [&_.swiper-pagination-bullet]:!opacity-100 [&_.swiper-pagination-bullet-active]:!bg-blue-600 [&_.swiper-pagination-bullet-active]:!w-6 [&_.swiper-pagination-bullet-active]:!rounded-[5px]`}
      >
        {items.map((item) => (
          <SwiperSlide key={item.id}>
            {type === 'image' ? (
              <div className="h-80 rounded-xl overflow-hidden">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={`Business ${item.id}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="bg-gray-200 border-2 border-dashed rounded-xl w-full h-full flex items-center justify-center">
                    <span className="text-gray-500">Image {item.id}</span>
                  </div>
                )}
              </div>
            ) : type === 'review' ? (
              <div className="bg-white p-6 rounded-xl shadow-lg h-full flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="border-2 rounded-3xl w-16 h-16">
                    <i
                      className="fa fa-user text-gray-500 text-4xl m-3"
                      aria-hidden="true"
                    ></i>
                  </div>
                  <div className="ml-4 mr-2">
                    <h4 className="font-bold text-lg">{item.name}</h4>
                  </div>
                </div>
                <p className="text-gray-700 flex-grow">"{item.review}"</p>
                <div className="flex mt-4">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className="fas fa-star text-yellow-400"
                    ></i>
                  ))}
                </div>
              </div>
            ) : type === 'service' ? (
              <div className="h-full">{item.content}</div>
            ) : null}
          </SwiperSlide>
        ))}
      </Swiper>

      {customNavigation && type === 'service' && (
        <>
          <button
            className={`swiper-button-prev-custom absolute top-1/2 -translate-y-1/2 ${
              isRTL ? 'right-0' : 'left-0'
            } z-10 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors md:flex hidden`}
            aria-label="Previous service"
          >
            <i
              className={`fas fa-chevron-${
                isRTL ? 'right' : 'left'
              } text-gray-700`}
            ></i>
          </button>
          <button
            className={`swiper-button-next-custom absolute top-1/2 -translate-y-1/2 ${
              isRTL ? 'left-0' : 'right-0'
            } z-10 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors md:flex hidden`}
            aria-label="Next service"
          >
            <i
              className={`fas fa-chevron-${
                isRTL ? 'left' : 'right'
              } text-gray-700`}
            ></i>
          </button>
        </>
      )}
    </>
  );
};

export default Carousel;
